import type { CommandReceipt, Conflict, ModelDocument, Operation } from '../shared/contracts';
import { applyOperations, operationConflicts, stableStringify } from '../core';

export interface PendingModelChange { operations: Operation[]; summary: string }
/** A normal acceptance can still normalize placement or naming on the server. */
export function modelSaveRequiresReconciliation(receipt: CommandReceipt, capturedDocument: ModelDocument) {
  return receipt.rebased || stableStringify(receipt.snapshot.document) !== stableStringify(capturedDocument);
}
/** Only the captured operations belong to this receipt. Later changes keep ownership of the live draft. */
export function acknowledgeModelSave<T extends PendingModelChange>(receipt: CommandReceipt, captured: readonly T[], capturedDocument: ModelDocument, current: {draft: ModelDocument; batches: T[]}) {
  const batches = current.batches.filter(batch => !captured.includes(batch));
  if (!modelSaveRequiresReconciliation(receipt, capturedDocument)) return {draft:current.draft, batches, conflicts:[] as Conflict[]};
  const operations = batches.flatMap(batch => batch.operations);
  const conflicts = operationConflicts(capturedDocument, receipt.snapshot.document, operations);
  if (conflicts.length) return {draft:current.draft, batches, conflicts};
  try {
    const draft = operations.length ? applyOperations(receipt.snapshot.document, operations).document : receipt.snapshot.document;
    return {draft, batches, conflicts};
  } catch (error) {
    // Persistence succeeded even though a newer local operation needs review.
    // Never retry the acknowledged command or replace the live draft on this path.
    return {draft:current.draft, batches, conflicts:[{path:'pending-changes', original:capturedDocument.model.name, local:operations, incoming:receipt.snapshot.revision, message:`Newer local changes need review: ${error instanceof Error ? error.message : String(error)}`} satisfies Conflict]};
  }
}
